import { Button, Group, Paper, SimpleGrid, Skeleton, Stack, Text, ThemeIcon } from '@mantine/core';
import type { Icon } from '@tabler/icons-react';
import { IconAlertTriangle, IconClipboardList, IconFileInvoice, IconGauge } from '@tabler/icons-react';
import { useSiteStatus } from '../hooks/useSiteStatus';
import { useTenantFeatures } from '../lib/tenantFeatures';

interface Tile {
  label: string;
  value: number | undefined;
  icon: Icon;
  /** Icon-chip tint in its normal state — drawn from Mantine's stock palette
   * (blue/violet/teal), not the app's amber/brandGreen/brandOrange/brandRed:
   * those four are reserved for document STATUS under the branded-neutral
   * ruling (2026-08-24), and these tiles are categories, not statuses. */
  color: string;
  /** True when a value of exactly 0 is a GOOD outcome (nothing outstanding) —
   * rendered with an affirmative tone instead of looking like a broken/empty
   * dashboard tile. Not every zero is good (e.g. zero open POs isn't
   * necessarily "caught up" — could just mean nothing's been ordered), so this
   * is opt-in per tile rather than a blanket "0 is fine" rule. */
  positiveWhenZero?: boolean;
}

/** Home-screen status strip (BUILD_SPEC.md §11): silo levels, open POs, uninvoiced
 * dispatches, low stock count from `GET /reporting/site-status`. Each tile renders as
 * a skeleton while loading, real data once useSiteStatus resolves, or — on failure —
 * a quiet "Unavailable" state with a Retry action. Previously isError fell into the
 * same skeleton branch as isLoading, so a failed fetch showed a permanent grey
 * skeleton indistinguishable from "still loading" and gave staff no way to recover
 * without a full page refresh.
 *
 * Design-uplift pass (2026-08-25): flat bordered rows replaced with proper stat
 * cards — icon chip, big number, label — and the two "nothing outstanding is
 * good" tiles (uninvoiced dispatches, low stock) get an explicit "All caught up"
 * caption at zero instead of a bare "0" that reads as an empty/broken widget.
 * Low stock additionally recolors its icon chip to the warning accent
 * (brandOrange) the moment it's non-zero — a genuine use of the branded-neutral
 * ruling's functional warning color, not decoration.
 */
export function SiteStatusStrip() {
  const { data, isLoading, isError, isFetching, refetch } = useSiteStatus();
  const { silosEnabled } = useTenantFeatures();
  const showSkeleton = isLoading || (!data && !isError);
  const lowStock = data?.lowStockCount;

  const tiles: Tile[] = [
    { label: 'Open POs', value: data?.openPurchaseOrders, icon: IconClipboardList, color: 'blue' },
    {
      label: 'Uninvoiced dispatches',
      value: data?.uninvoicedDispatches,
      icon: IconFileInvoice,
      color: 'violet',
      positiveWhenZero: true,
    },
    {
      label: 'Low stock items',
      value: lowStock,
      icon: IconAlertTriangle,
      color: lowStock ? 'brandOrange' : 'blue',
      positiveWhenZero: true,
    },
    // Hidden when the tenant's silo module is off (Stuart ruling 2026-08-26,
    // default OFF — Howick has none). The underlying `/reporting/site-status`
    // endpoint keeps returning `silos` regardless (UI-hiding is enough, per
    // this goal's scope), so `data?.silos` still resolves — just not rendered.
    ...(silosEnabled
      ? [{ label: 'Silos tracked', value: data?.silos.length, icon: IconGauge, color: 'teal' } satisfies Tile]
      : []),
  ];

  return (
    <Stack gap="sm">
      <Group justify="space-between">
        <Text fw={600} size="sm" c="dimmed" tt="uppercase" style={{ letterSpacing: '0.04em' }}>
          Site status
        </Text>
        {isError && (
          <Button variant="subtle" color="gray" size="compact-sm" loading={isFetching} onClick={() => refetch()}>
            Retry
          </Button>
        )}
      </Group>
      <SimpleGrid cols={{ base: 1, xs: 2, md: 4 }} spacing="md">
        {tiles.map((tile) => {
          const caughtUp = tile.positiveWhenZero && tile.value === 0;
          return (
            <Paper key={tile.label} withBorder radius="lg" p="md">
              <Group justify="space-between" align="flex-start" wrap="nowrap">
                <Stack gap={4} style={{ minWidth: 0 }}>
                  {isError ? (
                    <Text size="sm" c="dimmed" fs="italic">
                      Unavailable
                    </Text>
                  ) : showSkeleton ? (
                    <Skeleton height={32} width={48} />
                  ) : (
                    <Text fw={800} size="xl" lh={1} style={{ fontSize: '1.75rem' }}>
                      {tile.value}
                    </Text>
                  )}
                  <Text size="xs" c="dimmed" fw={600}>
                    {tile.label}
                  </Text>
                  {!isError && !showSkeleton && caughtUp && (
                    <Text size="xs" c="brandGreen" fw={600}>
                      All caught up
                    </Text>
                  )}
                </Stack>
                <ThemeIcon size={40} radius="md" variant="light" color={caughtUp ? 'brandGreen' : tile.color}>
                  <tile.icon size={20} stroke={1.5} />
                </ThemeIcon>
              </Group>
            </Paper>
          );
        })}
      </SimpleGrid>
    </Stack>
  );
}
